import { Injectable } from '@angular/core';
import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import { speechAppConfig } from '../../config/config'; //"./config";
import { KeyVaultService } from '../../service/key-vault.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { TraineeListService } from '../trainee-list.service';
import { ToastrService } from 'ngx-toastr';
const speechConfigKey = speechAppConfig.speechSubKey
const speechConfigEndpoint = speechAppConfig.speechEndpoint

export type PlaybackState = 'idle' | 'playing' | 'paused';

@Injectable({
  providedIn: 'root'
})
export class TextToSpeechService {
  private endpoint: string;
  private speechSynthesizer;
  private audioPlayer = new sdk.SpeakerAudioDestination();
  // Every SpeakerAudioDestination that has been created and could still be
  // producing audio. `configureSpeech()` builds a fresh player per utterance,
  // so a previously created player can be left "orphaned" and keep playing
  // audibly. Muting/stopping must silence ALL of them, not just the current
  // `audioPlayer`, otherwise voice keeps playing after the user hits mute
  // (JHUD-2939 / mute leak).
  private activeAudioPlayers: sdk.SpeakerAudioDestination[] = [];
  private isSpeaking = false;
  private textQueue = [];
  private testVoiceName: string="";
  // When true, any in-flight/queued synthesis is discarded so stopped/muted
  // speech never resumes or "leaks" into a later interaction (e.g. feedback,
  // unmuting the speaker, or a brand new roleplay session).
  private isStopped = false;
  private playbackTimer: any = null;
  private remainingPlaybackMs: number = 0;
  private playbackTimerStart: number = 0;
  // Persisted "speaker volume muted" state. configureSpeech() creates a brand
  // new SpeakerAudioDestination per utterance, so we must re-apply this flag to
  // every newly created player, otherwise a later Play/Stop/Play cycle spins up
  // an un-muted player and audio leaks while the user is still muted (JHUD-5132).
  private speakerVolumeMuted: boolean = false;
  // private endpoint = "wss://apim.medtechai.jnj.com/tts"
  // private endpoint = "wss://azr-hkt-hji-cmcrl-aiadvisor-speech-dev.cognitiveservices.azure.com/tts/cognitiveservices/websocket/v2";
  voiceName = 'en-US-AvaMultilingualNeural'
  lastSpokenText: string = '';
  lastVoiceName: string = '';
  textToSpeechEnabled$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  playbackState$: BehaviorSubject<PlaybackState> = new BehaviorSubject<PlaybackState>('idle');
  // Fires when audio actually starts streaming from the speaker (after the
  // network synthesis latency), as opposed to playbackState$ which flips to
  // 'playing' synchronously at request time. Used to drive an accurate
  // loading -> playing UI (e.g. the Play sample button in System Config).
  audioStarted$: Subject<void> = new Subject<void>();
  avatarInfo: { name: string; style: string; isEnabled: boolean; allocated_time: number; pending_time: number; };
  enableVoice: boolean = true;


  constructor(private readonly keyVaultService: KeyVaultService, private readonly traineeListService: TraineeListService,
    private readonly toastr: ToastrService
  ) {
    // this.initializeTTS();
  }

  private sanitizeTextForSpeech(text: string): string {
    if (!text) {
      return '';
    }

    // Add a pause cue after span content before stripping markup.
    let sanitized = text.replace(/<\/span>/gi, '. ');
    // Strip any HTML tags so markup is never spoken.
    sanitized = sanitized.replace(/<[^>]+>/g, ' ');
    // Remove markdown characters that are not useful in speech output.
    sanitized = sanitized.replace(/[#*]/g, ' ');

    return sanitized.replace(/\s+/g, ' ').trim();
  }

  public initializeTTS() {
    this.traineeListService.getCustomizeDetails().subscribe({
      next: (data: any) => {
        if(data) {
          this.enableVoice = data.enable_voice_interaction;
          this.voiceName = data.voice_title;
        }
      },
      error: (error) => {
        this.getSpeechKey();
      },
      complete: () => {
        this.getSpeechKey();
      }
    })
    

  }

  getSpeechKey() {
    this.keyVaultService.secrets$.pipe(take(1)).subscribe({
      next: (secrets) => {
        this.endpoint = secrets[speechConfigEndpoint] + `/ai_advisor_tts?business_unit_code=${sessionStorage.getItem('businessUnitCode')}&wwid=${sessionStorage.getItem('userId')}`;
      },
      error: (error) => {
        console.error('Error fetching speech key', error);
      },
      complete: () => {
        this.configureSpeech();
      }
    });
  }
  

  configureSpeech(testVoiceNameParam?: string | null) {
    this.testVoiceName = testVoiceNameParam;
    //const token = sessionStorage.getItem('bearer_token');
    const speechConfig = sdk.SpeechConfig.fromEndpoint(new URL(this.endpoint)); //eastus //fbaa193650404d1cbb26eb2f7dcf809c
  //  speechConfig.authorizationToken = token;
    speechConfig.speechSynthesisVoiceName = (this.testVoiceName !== "" && this.testVoiceName !== undefined) ? this.testVoiceName : this.voiceName;
    // const audioConfig = sdk.AudioConfig.fromDefaultSpeakerOutput();
    // Silence & discard any player created for a previous utterance before we
    // spin up a new one, so an orphaned player can never keep playing.
    this.silenceAllAudioPlayers();
    this.audioPlayer = new sdk.SpeakerAudioDestination();
    this.activeAudioPlayers.push(this.audioPlayer);
    // Re-apply the persisted speaker-mute state to this fresh player so audio
    // stays silent across Play/Stop/Play cycles until the user unmutes.
    if (this.speakerVolumeMuted) {
      try {
        this.audioPlayer.mute();
        if (this.audioPlayer.internalAudio) {
          this.audioPlayer.internalAudio.muted = true;
          this.audioPlayer.internalAudio.volume = 0;
        }
      } catch (e) {
        // player may not be fully initialized yet - ignore
      }
    }
    this.audioPlayer.onAudioStart = () => {
      // The underlying HTMLAudioElement is only created when playback actually
      // starts. Re-apply the persisted mute here so a fresh player spun up by a
      // Play/Stop/Play cycle stays silent until the user explicitly unmutes.
      if (this.speakerVolumeMuted) {
        try {
          this.audioPlayer.mute();
          if (this.audioPlayer.internalAudio) {
            this.audioPlayer.internalAudio.muted = true;
            this.audioPlayer.internalAudio.volume = 0;
          }
        } catch (e) {
          // ignore
        }
      }
      this.audioStarted$.next();
    };
    this.audioPlayer.onAudioEnd = () => {
      this.isSpeaking = false;
      if (this.textQueue.length === 0) {
        this.playbackState$.next('idle');
      }
    };
    const audioConfig = sdk.AudioConfig.fromSpeakerOutput(this.audioPlayer);
    this.speechSynthesizer = new sdk.SpeechSynthesizer(speechConfig, audioConfig);
    
  }

  public speak(text: string, voiceType?: string) {
    const sanitizedText = this.sanitizeTextForSpeech(text);
    if (!sanitizedText) {
      return;
    }

    if (this.isSpeaking) {
      this.textQueue.push(sanitizedText);
      return;
    }
    this.isStopped = false;
    this.lastSpokenText = sanitizedText;
    this.lastVoiceName = voiceType || '';
    this.isSpeaking = true;
    this.configureSpeech();
    this.synthesizeSpeech(sanitizedText, voiceType);
  }

  public replaySpeech(): void {
    if (this.lastSpokenText) {
      this.speak(this.lastSpokenText, this.lastVoiceName);
    }
  }

  /**
   * Play a short voice sample (e.g. the Play button in Create Profile).
   * A previous roleplay session may have called stopSpeech()/muteSpeech(),
   * which leaves `isStopped = true` on this root singleton and would cause
   * synthesizeSpeech() to drop the preview. Reset the playback state here so
   * the sample always plays regardless of the previous session (JHUD-4215).
   */
  public previewVoice(text: string, voiceName?: string): void {
    if (!text) {
      return;
    }
    this.isStopped = false;
    this.isSpeaking = false;
    this.textQueue = [];
    this.synthesizeSpeech(text, voiceName);
  }

  public synthesizeSpeech(text:string, voiceName?: string): void {
    // If playback has been stopped/muted, drop any in-flight or queued text so
    // it can never resume later (prevents leftover audio on unmute/feedback).
    if (this.isStopped) {
      this.textQueue = [];
      this.isSpeaking = false;
      return;
    }
    const sanitizedText = this.sanitizeTextForSpeech(text);
    if (!sanitizedText) {
      this.isSpeaking = false;
      this.playbackState$.next('idle');
      if (this.textQueue.length > 0) {
        const nextText = this.textQueue.shift();
        if (nextText) {
          this.synthesizeSpeech(nextText);
        }
      }
      return;
    }

    if(voiceName !== "" && voiceName !== undefined){
      this.configureSpeech(voiceName);
    }
    if (sanitizedText != undefined && sanitizedText != null) {
      if (!this.speechSynthesizer) {
        this.configureSpeech();
      }
      // Guard again in case stop/mute happened during (re)configuration.
      if (this.isStopped || !this.speechSynthesizer) {
        this.textQueue = [];
        this.isSpeaking = false;
        return;
      }
      this.playbackState$.next('playing');
      this.speechSynthesizer.speakTextAsync(sanitizedText,
        result => {
          if (result.reason === sdk.ResultReason.SynthesizingAudioCompleted) {
            // audioDuration is in 100-nanosecond ticks, convert to milliseconds
            const durationMs = result.audioDuration / 10000;
            this.startPlaybackTimer(durationMs);
          } else {
            console.error('Speech synthesis canceled, ' + result.errorDetails +
              '\nDid you set the speech resource key and region values?');
            this.isSpeaking = false;
            this.playbackState$.next('idle');
          }

          if (!this.isStopped && this.textQueue.length > 0) {
            let text_next = this.textQueue.shift()
            if (text_next != undefined && text_next != null)
              this.synthesizeSpeech(text_next)
          }
          //this.speechSynthesizer.close();
        },
        error => {
          console.error('Error:', error);
          this.isSpeaking = false;
          this.playbackState$.next('idle');
          //this.speechSynthesizer.close();
        }
      );
    }
  }

  public pauseSpeech(): void {
    if (this.playbackState$.value !== 'playing') {
      return;
    }
    this.audioPlayer.pause();
    this.clearPlaybackTimer();
    this.playbackState$.next('paused');
  }

  public resumeSpeech(): void {
    if (this.playbackState$.value !== 'paused') {
      return;
    }
    this.audioPlayer.resume();
    this.startPlaybackTimer(this.remainingPlaybackMs);
    this.playbackState$.next('playing');
  }

  public muteSpeech(): void {
    // Muting the speaker must fully halt any current/queued speech, otherwise
    // the buffered (silent) audio resumes at full volume once unmuted and the
    // "last" response gets read out over the current one (JHUD-2939).
    this.isStopped = true;
    this.textQueue = [];
    this.isSpeaking = false;
    this.clearPlaybackTimer();
    if (this.audioPlayer) {
      this.audioPlayer.mute();
      this.audioPlayer.pause();
    }
    // Also silence any orphaned players that may still be producing audio.
    this.silenceAllAudioPlayers();
    this.playbackState$.next('idle');
  }

  public unmutSpeech(): void {
    // Allow future speech again; nothing is resumed because muteSpeech()
    // already discarded any pending/queued audio.
    this.isStopped = false;
    this.audioPlayer.unmute();
  }

  /**
   * Silences (or restores) the speaker WITHOUT pausing playback, so the speech
   * keeps progressing while muted. On unmute the listener hears the avatar at
   * its current position instead of resuming from where it was muted. Applies
   * to every active player so no orphaned player leaks audio.
   */
  public setSpeakerVolumeMuted(muted: boolean): void {
    // Persist so newly created players (per-utterance) inherit the mute state.
    this.speakerVolumeMuted = muted;
    const players = [this.audioPlayer, ...(this.activeAudioPlayers || [])];
    for (const player of players) {
      if (!player) {
        continue;
      }
      try {
        if (muted) {
          player.mute();
        } else {
          player.unmute();
        }
        // Hard guarantee via the underlying HTMLAudioElement, keeping playback
        // running (do NOT pause) so the position continues to advance.
        if (player.internalAudio) {
          player.internalAudio.muted = muted;
          player.internalAudio.volume = muted ? 0 : 1;
        }
      } catch (e) {
        // player may already be closed - ignore
      }
    }
  }

  public resetAudio() {
    this.muteSpeech();
    this.stopSpeech();
    // Clear the persisted speaker-mute so a new session doesn't start muted.
    this.speakerVolumeMuted = false;
    this.testVoiceName = "";
    this.initializeTTS();
  }

  public stopAudio() {
    this.muteSpeech();
    this.stopSpeech();
    this.speakerVolumeMuted = false;
    this.testVoiceName = "";
  }

  private startPlaybackTimer(durationMs: number): void {
    this.clearPlaybackTimer();
    this.remainingPlaybackMs = durationMs;
    this.playbackTimerStart = Date.now();
    this.playbackTimer = setTimeout(() => {
      this.isSpeaking = false;
      if (this.textQueue.length === 0) {
        this.playbackState$.next('idle');
      }
    }, durationMs);
  }

  private clearPlaybackTimer(): void {
    if (this.playbackTimer) {
      clearTimeout(this.playbackTimer);
      this.playbackTimer = null;
      const elapsed = Date.now() - this.playbackTimerStart;
      this.remainingPlaybackMs = Math.max(0, this.remainingPlaybackMs - elapsed);
    }
  }

  /**
   * Silence EVERY SpeakerAudioDestination we have ever created, not just the
   * current `audioPlayer`. `configureSpeech()` builds a fresh player per
   * utterance, so a previous player can be orphaned while still buffering
   * audio. Muting/stopping only the current player let that orphan keep
   * playing audibly after the user hit mute (JHUD-2939). Muting + pausing +
   * closing each one guarantees complete silence.
   */
  private silenceAllAudioPlayers(): void {
    if (!this.activeAudioPlayers || this.activeAudioPlayers.length === 0) {
      return;
    }
    for (const player of this.activeAudioPlayers) {
      if (!player) {
        continue;
      }
      try {
        player.mute();
        // Force the underlying HTMLAudioElement silent as a hard guarantee in
        // case mute() is a no-op for the current playback state.
        if (player.internalAudio) {
          player.internalAudio.muted = true;
          player.internalAudio.volume = 0;
        }
      } catch (e) {
        // player may already be closed - ignore
      }
      try {
        player.pause();
      } catch (e) {
        // ignore
      }
      try {
        if (!player.isClosed) {
          player.close();
        }
      } catch (e) {
        // ignore
      }
    }
    // Keep only the current player reference so future utterances start clean.
    this.activeAudioPlayers = this.audioPlayer ? [this.audioPlayer] : [];
  }

  public stopSpeech(preserveLastText: boolean = false): void {
    this.isStopped = true; // block any in-flight/queued synthesis from resuming
    this.textQueue = []; // Clear the queue first to prevent any queued speech
    this.isSpeaking = false;
    // Clear the remembered text so a later session can't replay it
    // (e.g. Play button reading the previous roleplay's transcript - JHUD-4215).
    // When preserveLastText is true (manual Stop button), keep the text so the
    // user can replay the last utterance via the Play button.
    if (!preserveLastText) {
      this.lastSpokenText = '';
      this.lastVoiceName = '';
    }
    this.clearPlaybackTimer();
    this.playbackState$.next('idle');
    if (this.audioPlayer) {
      this.audioPlayer.pause();
      this.audioPlayer.close();
    }
    // Silence & close every player that could still be playing, including any
    // that were orphaned by a subsequent configureSpeech() call.
    this.silenceAllAudioPlayers();
    this.activeAudioPlayers = [];
    if (this.speechSynthesizer) {
      this.speechSynthesizer.close();
      this.speechSynthesizer = null;
    }
  }
}
